// Portfolio Administration v1.0 PR 4a §7.7 — "Assign vendor" modal.
//
// Picks from the global Vendor registry (PR 2a IVendorService) and
// records the assignment as a LoanPartyAssignment with
// counterpartyType=Vendor. The full per-loan VendorAssignment entity
// (fee schedule, executed agreement, performance score) ships with
// Vendor Program v3.0.

import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { VENDOR_ASSIGNABLE_STATUS } from "@/api/portfolioAdmin/client";
import {
  useAddLoanParty,
  useVendors,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";

interface AssignVendorModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
  /** Served from the required-vendor catalog (plus "Other", appended by
   *  the caller) so new assignments match the seeded VendorType strings
   *  exactly. */
  roleOptions: string[];
}

export function AssignVendorModal({
  loanId,
  open,
  onClose,
  roleOptions,
}: AssignVendorModalProps) {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [roleOnLoan, setRoleOnLoan] = useState(roleOptions[0]);
  const [notes, setNotes] = useState("");

  // Step 2 W1 — the picker now filters to the assignable status, so the
  // modal's "Pick from the active vendor registry" description is true
  // rather than aspirational. It was showing every non-archived vendor,
  // prospects and terminated suppliers included, and the server accepted
  // all of them. `AddPartyAsync` now refuses anything outside this set, so
  // the filter is convenience — the gate is server-side.
  const vendors = useVendors({ name: search, status: VENDOR_ASSIGNABLE_STATUS });
  const add = useAddLoanParty(loanId);

  const filtered = useMemo(() => (vendors.data ?? []).slice(0, 20), [vendors.data]);

  const onSubmit = async () => {
    if (!vendorId) {
      toast.error("Pick a vendor from the list.");
      return;
    }
    try {
      await add.mutateAsync({
        counterpartyType: "Vendor",
        counterpartyId: vendorId,
        roleOnLoan,
        notes: notes || null,
      });
      toast.success("Vendor assigned.");
      setVendorId("");
      setNotes("");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not assign vendor.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Assign vendor"
      description="Pick from the active vendor registry. Each assignment shows on the per-loan vendor list."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={!vendorId || add.isPending}
            data-testid="vendor-assign-submit"
          >
            Assign vendor
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="vendor-assign-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Find vendor</span>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
              aria-hidden
            />
            <input
              className="h-9 w-full pl-[28px] pr-space-2 border border-border-light rounded-md text-small"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="vendor-assign-search"
            />
          </div>
        </label>

        <div
          className="max-h-48 overflow-y-auto border border-border-light rounded-md"
          data-testid="vendor-assign-list"
        >
          {vendors.isLoading && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              Loading vendors…
            </p>
          )}
          {!vendors.isLoading && filtered.length === 0 && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              No vendors match.
            </p>
          )}
          {filtered.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVendorId(v.id)}
              className={`flex w-full items-center justify-between gap-space-2 px-space-2 py-space-1 text-left hover:bg-white-warm transition-colors ${
                vendorId === v.id ? "bg-gold/10" : ""
              }`}
              data-testid={`vendor-assign-option-${v.id}`}
            >
              <span className="text-small">{v.name}</span>
              <span className="text-caption text-gray-mid">{v.vendorType ?? "—"}</span>
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Role on loan</span>
          <select
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={roleOnLoan}
            onChange={(e) => setRoleOnLoan(e.target.value)}
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Notes</span>
          <textarea
            className="min-h-[5rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
}
